import React from 'react';
import { Skeleton } from 'primereact/skeleton';
import styles from './Overview.module.scss';
import BarChart from '../barChart/BarChart';
import MyErrorMapper from '../../../../utils/MyErrorMapper';
import useTenantStore from '../../../../stores/TenantStore';
import { noop } from '../../../../utils/GeneralUtils';
import OpenAPI from '../../../../endpoint/Openapi';
import { type paths } from '../../../../endpoint/Schema';
import { useTestUsers } from '../../../../contexts/TestUsersContext';
import { useNotCustomerUsers } from '../../../../contexts/NotCustomerUsersContext';

const Overview: React.FC = () => {
  const { id: tenantId, appColors } = useTenantStore();
  const { includeTestUsers } = useTestUsers();
  const { includeNotCustomerUsers } = useNotCustomerUsers();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] =
    React.useState<
      paths['/api/v2/admin/report/overview']['get']['responses']['200']['content']['application/json']
    >();
  const [error, setError] = React.useState<unknown>();

  React.useEffect(() => {
    const getOverview = async () => {
      setLoading(true);

      try {
        const res = await OpenAPI.GET('/api/v2/admin/report/overview', {
          params: {
            query: { tenantId, includeTestUsers, includeNotCustomerUsers },
          },
        });

        if (res.data) setData(res.data);
        else setError(res.error);
      } catch (err) {
        setError(err);
      }

      setLoading(false);
    };

    getOverview().catch(noop);
  }, [tenantId, includeTestUsers, includeNotCustomerUsers]);

  function renderPeriodColor(
    color?: string | null,
    label?: string,
  ): JSX.Element {
    return (
      <div className={styles.periodContainer}>
        {loading ? (
          <Skeleton width="2rem" height="2rem" borderRadius="0.25rem" />
        ) : (
          <div
            style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '0.25rem',
              backgroundColor: color ?? 'black',
            }}
          />
        )}

        <span className={styles.periodLabel}>{label}</span>
      </div>
    );
  }

  if (error != null)
    return (
      <>
        <div className={styles.title}>Overview</div>
        <div className={styles.chartsWrapper}>
          Error occurred:
          <br />
          {new MyErrorMapper(error).getMessage()}
        </div>
      </>
    );

  return (
    <>
      <div className={styles.title}>Overview</div>

      <div className={styles.chartsWrapper}>
        <div className={styles.chartsContainer}>
          <BarChart
            label="Driving score"
            lastWeek={data?.lastWeekScore?.safetyScore ?? 0}
            monthToDate={data?.monthToDate?.safetyScore ?? 0}
            lastMonth={data?.lastMonth?.safetyScore ?? 0}
            yearToDate={data?.yearToDate?.safetyScore ?? 0}
            loading={loading}
          />
          <BarChart
            label="Eco score"
            lastWeek={data?.lastWeekScore?.greenScore ?? 0}
            monthToDate={data?.monthToDate?.greenScore ?? 0}
            lastMonth={data?.lastMonth?.greenScore ?? 0}
            yearToDate={data?.yearToDate?.greenScore ?? 0}
            loading={loading}
          />
          <BarChart
            label="Speeding"
            lastWeek={data?.lastWeekScore?.speedingScore ?? 0}
            monthToDate={data?.monthToDate?.speedingScore ?? 0}
            lastMonth={data?.lastMonth?.speedingScore ?? 0}
            yearToDate={data?.yearToDate?.speedingScore ?? 0}
            loading={loading}
          />
          <BarChart
            label="Acceleration"
            lastWeek={data?.lastWeekScore?.accelerationScore ?? 0}
            monthToDate={data?.monthToDate?.accelerationScore ?? 0}
            lastMonth={data?.lastMonth?.accelerationScore ?? 0}
            yearToDate={data?.yearToDate?.accelerationScore ?? 0}
            loading={loading}
          />
          <BarChart
            label="Phone usage"
            lastWeek={data?.lastWeekScore?.phoneUsageScore ?? 0}
            monthToDate={data?.monthToDate?.phoneUsageScore ?? 0}
            lastMonth={data?.lastMonth?.phoneUsageScore ?? 0}
            yearToDate={data?.yearToDate?.phoneUsageScore ?? 0}
            loading={loading}
          />
          <BarChart
            label="Cornering"
            lastWeek={data?.lastWeekScore?.corneringScore ?? 0}
            monthToDate={data?.monthToDate?.corneringScore ?? 0}
            lastMonth={data?.lastMonth?.corneringScore ?? 0}
            yearToDate={data?.yearToDate?.corneringScore ?? 0}
            loading={loading}
          />
          <BarChart
            label="Braking"
            lastWeek={data?.lastWeekScore?.brakingScore ?? 0}
            monthToDate={data?.monthToDate?.brakingScore ?? 0}
            lastMonth={data?.lastMonth?.brakingScore ?? 0}
            yearToDate={data?.yearToDate?.brakingScore ?? 0}
            loading={loading}
          />
        </div>

        <div className={styles.periodWrapper}>
          {renderPeriodColor(
            appColors?.find((c) => c.type === 'P400')?.value,
            'Last week',
          )}
          {renderPeriodColor(
            appColors?.find((c) => c.type === 'P500')?.value,
            'Month to date',
          )}
          {renderPeriodColor(
            appColors?.find((c) => c.type === 'P300')?.value,
            'Last month',
          )}
          {renderPeriodColor(
            appColors?.find((c) => c.type === 'P200')?.value,
            'Year to date',
          )}
        </div>
      </div>
    </>
  );
};

export default Overview;
